'use client'

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import type { StoryData } from '@/content/stories'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { IconArrowRight, IconLock } from '@tabler/icons-react'
import { useEntitlements } from '@/hooks/useEntitlements'
import { UpgradeIntentDialog } from '@/components/paywall/upgrade-intent-dialog'
import { analytics } from '@/lib/analytics'

interface PremiumChapterGateProps {
  story: StoryData
  surface: string
  children: ReactNode
}

function buildPreview(story: StoryData, maxSentences: number = 2): string {
  const sentences: string[] = []

  for (const paragraph of story.paragraphs) {
    for (const sentence of paragraph.sentences) {
      const text = sentence.tokens.map((token) => token.s).join('')
      if (text.trim()) {
        sentences.push(text)
      }
      if (sentences.length >= maxSentences) {
        return sentences.join(' ')
      }
    }
  }

  return sentences.join(' ')
}

export function PremiumChapterGate({
  story,
  surface,
  children
}: PremiumChapterGateProps) {
  const { isPremium, premiumChaptersEnabled, pricing } = useEntitlements()
  const [openDialog, setOpenDialog] = useState(false)

  const isLocked =
    premiumChaptersEnabled && Boolean(story.isPremiumChapter) && !isPremium

  const chapterNumber = story.chapterNumber ?? 0
  const previewText = useMemo(() => buildPreview(story), [story])

  useEffect(() => {
    if (!isLocked) return
    analytics.premiumChapterLocked(story.slug, chapterNumber, surface, {
      series_id: story.seriesId ?? null,
      chapter_count: story.chapterCount ?? null
    })
    analytics.paywallViewed('premium-chapters', surface, {
      story_slug: story.slug,
      chapter_number: chapterNumber,
      limit: 1,
      current_usage: chapterNumber
    })
  }, [
    isLocked,
    story.slug,
    chapterNumber,
    surface,
    story.seriesId,
    story.chapterCount
  ])

  if (!isLocked) {
    return <>{children}</>
  }

  return (
    <>
      <div className="relative min-w-0">
        {/* Soft gate by design: keep story text rendered for SEO crawlability. */}
        <div
          className="pointer-events-none blur-[5px] select-none"
          aria-hidden="true"
        >
          {children}
        </div>
        <div className="pointer-events-none absolute inset-0 z-30 px-4 pt-24 sm:pt-32">
          <div className="bg-background/95 supports-[backdrop-filter]:bg-background/88 ring-border/70 pointer-events-auto sticky top-[calc(var(--product-shell-header-height)+5rem)] mx-auto w-full max-w-xl rounded-xl border p-5 shadow-2xl ring-1 shadow-black/10 backdrop-blur-xl sm:p-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className="gap-1 border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
              >
                <IconLock className="h-3 w-3" />
                Pro story
              </Badge>
              {story.chapterNumber && story.chapterCount && (
                <Badge variant="secondary">
                  Chapter {story.chapterNumber}/{story.chapterCount}
                </Badge>
              )}
            </div>
            <h2 className="mt-4 text-2xl leading-tight font-semibold tracking-tight">
              Unlock {story.title.s}
            </h2>
            <p className="text-muted-foreground mt-2 text-sm leading-6">
              Pro unlocks this story in the guided reader. The dictionary,
              document reader, and offline saves are free for everyone.
            </p>
            {previewText && (
              <p className="bg-muted/45 text-muted-foreground mt-4 line-clamp-2 rounded-lg px-3 py-2 text-sm leading-6">
                {previewText}
              </p>
            )}
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button
                onClick={() => {
                  analytics.paywallCtaClicked('premium-chapters', surface, {
                    story_slug: story.slug,
                    chapter_number: chapterNumber
                  })
                  setOpenDialog(true)
                }}
              >
                Unlock this chapter
                <IconArrowRight className="h-4 w-4" />
              </Button>
              <p className="text-muted-foreground text-xs">
                From ${pricing.weeklyUsd.toFixed(2)}/week.
              </p>
            </div>
          </div>
        </div>
      </div>

      <UpgradeIntentDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        paywallType="premium-chapters"
        surface={surface}
        context={{
          story_slug: story.slug,
          chapter_number: story.chapterNumber ?? null,
          chapter_count: story.chapterCount ?? null
        }}
        title="Unlock Pro stories"
        description="Read selected graded stories and chapters in the full reader. All learning tools remain free."
        submitLabel="Unlock Pro stories"
      />
    </>
  )
}
